// ----------------------------------------------------------------------
// One date format for the billing surfaces.
//
// `src/utils/format-time.ts` owns date formatting app-wide (dayjs, `DD MMM YYYY`), but no billing
// component uses it — the area grew six separate inline `toLocaleDateString` calls instead, and
// `{ month: 'short', day: 'numeric', year: 'numeric' }` is the one three of them already agree on.
// This is that format, named once, so new billing copy stops adding variants. Adopting `fDate`
// across the whole area is a worthwhile follow-up; doing it here would print "17 Apr 2026" next to
// siblings printing "Apr 17, 2026".
// ----------------------------------------------------------------------

/** "Apr 17, 2026". Returns an em dash for a missing or unparseable date. */
export function formatBillingDate(iso: string | null | undefined): string {
  if (!iso) return '—';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** "April 17, 2026" — the long form, for a one-off confirmation rather than a dense card. */
export function formatBillingDateLong(iso: string | null | undefined): string {
  if (!iso) return '—';

  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * The same calendar day N months out, clamped to the end of the target month.
 * <p>
 * `setMonth(getMonth() + n)` rolls over — 31 Jan + 1 month lands on 3 March — which would promise
 * more free time than the server grants, since .NET's `AddMonths` clamps to 28 February.
 */
export function addMonthsClamped(from: Date, months: number): Date {
  const target = new Date(from.getTime());
  const day = target.getDate();

  target.setDate(1);
  target.setMonth(target.getMonth() + months);

  const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();

  target.setDate(Math.min(day, lastDayOfTargetMonth));
  return target;
}
